import { type LocalSettings, type MainSettings, ScreenType } from "@client/types";
import {
  type StyleSheetColorType,
  type StyleSheetData,
  type StyleSheetSettings,
  type StyleSheetStructureItem,
  type StyleSheetType
} from "@client/types/StyleSheet";
import { findInNodes, updateNode } from "@common/lib/tree";
import { isColorLike } from "./color";
import { generateId } from "./random";
import { getInherittedSettingValueV2 } from "./settings";

export const STYLE_SHEET_IDS = {
  defaults: {
    primaryColor: "XMJeMIu1URBH",
    textColor: "N52QH3TBZPnO",
    h1: "25sxq2D6mQWx",
    h2: "aqVtqV3SPbOV",
    h3: "sP6Go36TYis",
    h4: "WbkALHrXtcpl",
    h5: "QuMQ9gzTkDrr",
    h6: "LwF8N53Fo4Vf",
    link: "HpmnTnleIL1f",
    text: "l6c1GtRli4W6"
  },
  custom: "w8vULATKsJin"
};

export const isDefaultStyleSheetId = (id: string) => Object.values(STYLE_SHEET_IDS.defaults).includes(id);
export const getDefaultStyleSheetId = (type: StyleSheetType, subType?: string | null) => {
  if (type === "color") {
    switch (subType) {
      case "primary":
        return STYLE_SHEET_IDS.defaults.primaryColor;
      case "text":
        return STYLE_SHEET_IDS.defaults.textColor;
      default:
        throw new Error(`BUG. Unknown style sheet subtype ${subType}`);
    }
  }
  return STYLE_SHEET_IDS.defaults[type];
};

export const getStyleSheetColor = (
  styleSheetSettings: StyleSheetSettings,
  localStyleSheetSettings: LocalSettings,
  value: string | undefined,
  screen: ScreenType,
  fallbackColor: StyleSheetColorType | string
) => {
  // HACK: 1001 uses background: ... CSS in color values. TODO: get rid of this
  if (isColorLike(value) || value?.toString().startsWith("background")) return value;

  let finalValue = value && value in styleSheetSettings ? value : undefined;

  if (!finalValue) {
    switch (fallbackColor) {
      case "primary": {
        finalValue = STYLE_SHEET_IDS.defaults.primaryColor;
        break;
      }
      case "text": {
        finalValue = STYLE_SHEET_IDS.defaults.textColor;
        break;
      }
      default:
        return fallbackColor;
    }
  }

  return getInherittedSettingValueV2(
    styleSheetSettings[finalValue],
    localStyleSheetSettings ? localStyleSheetSettings[finalValue] : undefined,
    undefined,
    "color",
    screen
  );
};

export const upsertFolder = (structure: StyleSheetStructureItem[], parentId: string, label: string): string => {
  const parentFolder = findInNodes(structure, (x) => x.id === parentId);
  if (!parentFolder) throw new Error("BUG");

  const folder = parentFolder.children?.find((x) => x.label === label);
  if (folder) return folder.id;

  const id = generateId();
  parentFolder.children = [
    ...(parentFolder.children ?? []),
    {
      id,
      label,
      children: []
    }
  ];
  return id;
};

export const upsertItemStructure = (
  structure: StyleSheetStructureItem[],
  parentId: string,
  id: string,
  label: string,
  type: StyleSheetType | null
) =>
  updateNode(structure, parentId, (x) => {
    if (x.children?.some((y) => y.id === id)) return x;

    return {
      ...x,
      children: [
        ...(x.children ?? []),
        {
          id,
          label,
          type: type ?? undefined,
          children: type ? undefined : []
        }
      ]
    };
  });

export const addItemSettings = (styleSheetSettings: StyleSheetSettings, id: string, settings: MainSettings) => ({
  ...styleSheetSettings,
  [id]: settings
});

export const addItem = (
  styleSheet: StyleSheetData,
  id: string,
  parentId: string,
  type: StyleSheetType | null,
  label: string,
  settings?: MainSettings
): StyleSheetData => {
  let newStyleSheet = {
    ...styleSheet,
    structure: upsertItemStructure(styleSheet.structure, parentId, id, label, type)
  };

  if (settings) {
    newStyleSheet = {
      ...newStyleSheet,
      settings: addItemSettings(newStyleSheet.settings, id, settings)
    };
  }

  return newStyleSheet;
};

export const getStyleSheetTypeById = (styleSheet: StyleSheetData, id: string): StyleSheetType | undefined =>
  findInNodes(styleSheet.structure, (x) => x.id === id)?.type;
